import React, { useState, useEffect, useCallback } from 'react';

// Dialog Context for global access
const DialogContext = React.createContext(null);

export function DialogProvider({ children }) {
  const [dialogs, setDialogs] = useState([]);

  const showDialog = useCallback((options) => {
    return new Promise((resolve) => {
      const id = Date.now() + Math.random();
      setDialogs(prev => [...prev, { ...options, id, resolve }]);
    });
  }, []);

  const closeDialog = useCallback((id, result) => {
    setDialogs(prev => {
      const dialog = prev.find(d => d.id === id);
      if (dialog) {
        dialog.resolve(result);
      }
      return prev.filter(d => d.id !== id);
    });
  }, []);

  // Confirm dialog
  const confirm = useCallback((message, title = 'Potwierdzenie') => {
    return showDialog({ type: 'confirm', title, message });
  }, [showDialog]);

  // Prompt dialog
  const prompt = useCallback((message, defaultValue = '', title = 'Wprowadź dane') => {
    return showDialog({ type: 'prompt', title, message, defaultValue });
  }, [showDialog]);

  // Alert dialog
  const alert = useCallback((message, title = 'Informacja') => {
    return showDialog({ type: 'alert', title, message });
  }, [showDialog]);

  return (
    <DialogContext.Provider value={{ confirm, prompt, alert }}>
      {children}
      <div className="dialog-container">
        {dialogs.map(dialog => (
          <DialogModal
            key={dialog.id}
            {...dialog}
            onClose={(result) => closeDialog(dialog.id, result)}
          />
        ))}
      </div>
    </DialogContext.Provider>
  );
}

export function useDialog() {
  const context = React.useContext(DialogContext);
  if (!context) {
    throw new Error('useDialog must be used within DialogProvider');
  }
  return context;
}

function DialogModal({ type, title, message, defaultValue = '', onClose }) {
  const [inputValue, setInputValue] = useState(defaultValue);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    // Focus input on prompt
    if (type === 'prompt') {
      const input = document.querySelector('.dialog-input');
      if (input) {
        input.focus();
        input.select();
      }
    }
    
    // Escape key handler
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        handleClose(type === 'alert' ? true : null);
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [type]);

  const handleClose = (result) => {
    setIsClosing(true);
    setTimeout(() => onClose(result), 200);
  };

  const handleConfirm = () => {
    if (type === 'prompt') {
      handleClose(inputValue || null);
    } else {
      handleClose(true);
    }
  };

  const handleCancel = () => {
    handleClose(type === 'alert' ? true : null);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleConfirm();
    }
  };

  return (
    <div className={`dialog-overlay ${isClosing ? 'dialog-overlay--closing' : ''}`} onClick={handleCancel}>
      <div className={`dialog-modal ${isClosing ? 'dialog-modal--closing' : ''}`} onClick={e => e.stopPropagation()}>
        <button className="dialog-close" onClick={handleCancel} aria-label="Zamknij">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        
        <h2 className="dialog-title">{title}</h2>
        
        {message && <p className="dialog-message">{message}</p>}
        
        {type === 'prompt' && (
          <input
            type="text"
            className="dialog-input"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Wprowadź wartość..."
          />
        )}
        
        <div className="dialog-actions">
          {type === 'alert' ? (
            <button className="dialog-btn dialog-btn--primary" onClick={handleConfirm}>
              OK
            </button>
          ) : (
            <>
              <button className="dialog-btn dialog-btn--primary" onClick={handleConfirm}>
                {type === 'confirm' ? 'Tak' : 'OK'}
              </button>
              <button className="dialog-btn dialog-btn--secondary" onClick={handleCancel}>
                {type === 'confirm' ? 'Nie' : 'Anuluj'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default DialogProvider;
